import React from 'react';
import { Link } from 'react-router-dom';
import LegalLayout from '../components/LegalLayout';
import { SUPPORT_EMAIL } from '../config/support';
import usePageMeta from '../hooks/usePageMeta';

// ---------------------------------------------------------------------------
// NOT LEGAL ADVICE. This page exists so the product has a truthful, publicly
// reachable privacy policy - both Google Play and the Microsoft Store require
// one, and the data inventory below is drawn from an audit of what the code
// actually collects and sends (audit B3 / Findings 5.4a, 4.7).
//
// It has NOT been reviewed by an attorney and is not a substitute for that
// review. Before taking payment from customers outside Wisconsin, or from any
// customer in a state or country with its own privacy statute, have a lawyer
// read it.
//
// If you add a third-party service, a new data field, or a new place data is
// sent, update this page in the same commit. A privacy policy that has fallen
// behind the code is worse than none.
// ---------------------------------------------------------------------------

// Every third party that receives data, from server/services and the client.
const PROCESSORS = [
  {
    name: 'Stripe',
    purpose: 'Subscription billing for your SnowTrack plan',
    data: 'Your name, email address, business name and card details. Card details go to Stripe directly and never reach us.',
  },
  {
    name: 'Square',
    purpose: 'Invoicing your own customers, from your own Square account',
    data: 'Your customer’s name, email and phone, and the amounts and line items on each invoice you send',
  },
  {
    name: 'SendGrid',
    purpose: 'Transactional and bulk email delivery',
    data: 'Recipient email address and message contents',
  },
  {
    name: 'Twilio',
    purpose: 'Text message (SMS) delivery to customers who opted in',
    data: 'Recipient mobile number and message contents',
  },
  {
    name: 'Cloudinary',
    purpose: 'Photo storage and delivery',
    data: 'Photos of serviced properties uploaded by service providers',
  },
  {
    name: 'Google Maps Platform',
    purpose: 'Address autocomplete, geocoding and route optimization',
    data: 'Service addresses and device location',
  },
  {
    name: 'OpenStreetMap / Nominatim',
    purpose: 'Reverse geocoding (turning coordinates into an address)',
    data: 'Device latitude and longitude',
  },
  {
    name: 'NOAA / National Weather Service (api.weather.gov)',
    purpose: 'Weather forecasts and storm data',
    data: 'Approximate coordinates only. No personal information is sent.',
  },
  {
    name: 'Railway',
    purpose: 'Backend application and database hosting',
    data: 'All account and service data, as the hosting provider',
  },
  {
    name: 'Netlify',
    purpose: 'Frontend web hosting and content delivery',
    data: 'Standard web request data, including IP address',
  },
];

function Privacy() {
  usePageMeta({
    title: 'Privacy Policy | SnowTrack',
    description: 'What SnowTrack collects, why, which services see it, and how to have it deleted. Written for snow removal contractors and their customers.',
    path: '/privacy',
  });

  return (
    <LegalLayout
      title="Privacy Policy"
      subtitle="What we collect, why we collect it, who else sees it, and how to get rid of it."
      updated="October 22, 2026"
    >
      <p>
        <strong>SnowTrack&trade; is operated by CW Affiliate Investments, LLC.</strong>
      </p>

      <div className="legal-callout">
        <p>
          <strong>The short version.</strong> We collect what the app needs to run your snow
          removal business: your account details, your customers and their service addresses,
          service and payment history, photos your crews take, and your device location while
          you are routing. We do not sell your data or your customers&apos; data to anyone. You can
          delete your company and everything in it at any time.
        </p>
      </div>

      <h2>Who we are</h2>
      <p>
        SnowTrack™ is a business management platform for snow removal contractors, operated from
        the State of Wisconsin, United States. This policy covers the SnowTrack™ web app, the
        installable app built from it, and the customer portal.
      </p>
      <p>
        Two kinds of people appear in this policy. <strong>Service providers</strong> are the
        contractors who sign up, pay for a plan, and enter their own customers.{' '}
        <strong>Property customers</strong> are the homeowners and businesses those contractors
        serve. If you are a property customer, your contractor entered your details and is
        responsible for them; we hold and process that data on their behalf. Contact your
        contractor first, or write to us and we will point you at them.
      </p>

      <h2>What we collect</h2>

      <h3>Account information</h3>
      <ul>
        <li>Your name, email address, phone number and business name</li>
        <li>Your business address</li>
        <li>A password, stored only as a bcrypt hash - we cannot read it</li>
        <li>The plan you selected when you registered</li>
      </ul>

      <h3>Your Square connection</h3>
      <p>
        If you connect Square from Settings, you are connecting <strong>your own Square
        account</strong>, not ours. We store the access token Square issues, encrypted, along
        with the Square location you chose and the merchant id of your account. We use them for
        one thing: creating and sending the invoices you ask us to send. You can disconnect at
        any time from Settings, which revokes the token with Square and deletes it from our
        database.
      </p>

      <h3>Payments</h3>
      <p>
        Two different payments happen in SnowTrack™ and they use two different companies.
      </p>
      <p>
        <strong>Your subscription to us</strong> is processed by <strong>Stripe</strong>. When
        you start or change a plan you are sent to Stripe&apos;s own checkout page and you enter
        your card there. We never see or store your card number. Stripe tells us which plan you
        are on, whether the payment succeeded, and when the period ends.
      </p>
      <p>
        <strong>The invoices you send your customers</strong> are processed by{' '}
        <strong>Square</strong>, through the Square account you connected. That money goes to
        you, not to us. We never hold your customers&apos; funds and we never see their card
        details.
      </p>

      <h3>Your customer records</h3>
      <ul>
        <li>Customer names, email addresses and phone numbers</li>
        <li>Service addresses for each property, including additional properties</li>
        <li>Pricing, notes, service preferences and referral relationships</li>
        <li>Service history: what was done, when, by whom, and whether it was skipped</li>
        <li>Payment status, invoice amounts and payment dates</li>
      </ul>

      <h3>Photos</h3>
      <p>
        Crews can take photos at a stop to record work done or damage found. These are photos of
        customer property, uploaded from the device camera and stored with Cloudinary. Only take
        photos you need for the job, and tell your customers you do it.
      </p>

      <h3>Precise device location</h3>
      <p>
        With your permission, the app reads your device&apos;s <strong>precise GPS location</strong>{' '}
        to optimize the remaining stops on a route from where you actually are, and to look up
        local weather and storm conditions. Location is read only while you are using those
        features. We do not track crew location in the background, we do not build location
        history, and there is no live crew-tracking feature. You can refuse or revoke the
        permission in your browser or device settings; route optimization then starts from the
        route&apos;s configured starting address instead, or from your last completed stop if you
        have already completed one.
      </p>

      <h3>Technical information</h3>
      <ul>
        <li>Browser type and version, operating system and screen size</li>
        <li>IP address, recorded by our hosting providers in ordinary server logs</li>
        <li>Pages viewed and actions taken inside the app, for troubleshooting</li>
      </ul>
      <p>
        We do not use advertising trackers or third-party analytics cookies. The app stores a
        login token and a few interface preferences in your browser&apos;s local storage; clearing
        your browser data logs you out.
      </p>

      <h2>Why we collect it</h2>
      <ul>
        <li>To create and run your account, and to authenticate you</li>
        <li>To provide the features you asked for: routing, invoicing, reporting, the portal</li>
        <li>To send transactional email - invitations, password resets, invoices, notifications</li>
        <li>
          If you choose to receive SnowTrack product email, to send it to your login email. It is
          off unless you tick the box at signup or turn it on in Settings, Account. Every message
          has an unsubscribe link, and you can turn it off in Settings, Account at any time.
        </li>
        <li>
          To take payment for your SnowTrack™ subscription through Stripe, and to create the
          invoices you send your own customers through your connected Square account
        </li>
        <li>To diagnose faults and keep the service secure and available</li>
        <li>To answer your support requests</li>
      </ul>
      <p>
        We do not use your data or your customers&apos; data to train machine learning models, and we
        do not use it to market to your customers.
      </p>

      <h2>Who else sees it</h2>
      <p>
        We use the third-party services below to run SnowTrack™. Each one receives only the data
        it needs for its purpose, and each is bound by its own terms and privacy commitments.
      </p>
      <div className="legal-table-wrapper">
        <table className="legal-table">
          <thead>
            <tr>
              <th>Service</th>
              <th>What it does for us</th>
              <th>What it receives</th>
            </tr>
          </thead>
          <tbody>
            {PROCESSORS.map((p) => (
              <tr key={p.name}>
                <td><strong>{p.name}</strong></td>
                <td>{p.purpose}</td>
                <td>{p.data}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p>
        The dashboard also loads weather icon images directly from OpenWeather. That request comes
        from your browser, so OpenWeather can see your IP address. No account or customer data is
        included in it.
      </p>
      <p>
        Beyond those services, we disclose data only where we are legally required to - a valid
        subpoena, court order or comparable legal process - or where it is necessary to
        investigate fraud or abuse of the service. If SnowTrack™ is ever sold or merged, your data
        would transfer with the business, and we would tell you before that happened.
      </p>

      <h2>What we never do</h2>
      <ul>
        <li><strong>We do not sell your data or your customers&apos; data to third parties.</strong></li>
        <li>We do not rent, trade or share it with data brokers or advertisers.</li>
        <li>We do not contact your customers for our own marketing purposes.</li>
      </ul>

      <h2>Where your data lives</h2>
      <p>
        Your data is stored on servers located in the <strong>United States</strong>. Our backend
        and database are hosted with Railway and the frontend with Netlify. If you use SnowTrack™
        from outside the United States, your data is transferred to and stored in the United
        States.
      </p>

      <h2>The customer portal</h2>
      <p>
        Your customers can be given a login to a portal where they see their own service history
        and their own invoices, and nothing else. The portal shows one customer their own records
        only. It does not take payment. When an invoice is unpaid, the portal links out to the
        Square invoice page your Square account generated, and payment happens there, between
        your customer and Square.
      </p>

      <h2>Text messages</h2>
      <p>
        If your contractor offers text updates and you opt in, they can send you service messages
        by SMS, such as a notice that a plow driver is on the way to your address, service and
        billing updates, and seasonal confirmations. Message frequency varies and depends on
        weather; expect more messages during storms and few or none between them. Message and
        data rates may apply. Reply STOP at any time to stop receiving messages, or HELP for help.
        We record when and how you opted in, and the wording you agreed to, so your contractor
        can prove consent.
      </p>
      <p>
        Mobile phone numbers and text message opt-in information are never sold, rented, or
        shared with third parties or affiliates for marketing or promotional purposes. They are
        used only to send the messages described above, through our messaging provider, Twilio.
      </p>

      <h2>How long we keep it</h2>
      <ul>
        <li>
          <strong>While your account is active:</strong> we keep your data so the product works -
          service history and invoices are the point of the product.
        </li>
        <li>
          <strong>After you delete your account:</strong> your company record, users, customers,
          properties, routes, services, invoices and expenses are removed from the live database
          immediately.
        </li>
        <li>
          <strong>Backups:</strong> deleted data can persist in encrypted database backups for up
          to 30 days before those backups roll off.
        </li>
        <li>
          <strong>Your Square connection:</strong> disconnecting in Settings revokes the token
          with Square and deletes it from our database immediately. Deleting your company deletes
          it too.
        </li>
        <li>
          <strong>Email and server logs:</strong> delivery records and request logs are retained
          for a short period for troubleshooting and abuse investigation.
        </li>
      </ul>

      <h2>Security</h2>
      <ul>
        <li>All traffic is encrypted in transit with HTTPS</li>
        <li>Passwords are stored as bcrypt hashes, never in plain text</li>
        <li>Email invitation and password-reset tokens are stored hashed and expire</li>
        <li>Every request is scoped to your company, so tenants cannot read each other&apos;s data</li>
        <li>
          We never see or store card numbers. Your subscription card is held by Stripe, and your
          customers&apos; cards are held by Square.
        </li>
      </ul>
      <p>
        No system is perfectly secure. If we discover a breach affecting your data, we will tell
        you promptly and describe what happened.
      </p>

      <h2>Your choices and your rights</h2>
      <ul>
        <li>
          <strong>See your data.</strong> Almost all of it is visible in the app. Email us for
          anything that is not.
        </li>
        <li>
          <strong>Correct it.</strong> Edit your profile, company details, customers and services
          directly in the app.
        </li>
        <li>
          <strong>Export it.</strong> Reports and customer lists can be exported from the app. Ask
          us if you need a fuller export.
        </li>
        <li>
          <strong>Stop product email.</strong> Use the unsubscribe link in any SnowTrack product
          email, or turn it off in Settings, Account. Account email, such as password resets and
          billing notices, still arrives.
        </li>
        <li>
          <strong>Delete it.</strong> A company owner can delete the company and everything in it
          from <strong>Settings &rarr; Delete Account</strong>. It is immediate and cannot be
          undone.
        </li>
        <li>
          <strong>Request deletion without logging in.</strong> Use the{' '}
          <Link to="/delete-account">account deletion request page</Link>.
        </li>
        <li>
          <strong>Turn off location.</strong> Refuse or revoke the browser or device location
          permission. Routing and weather still work, from the route&apos;s starting address.
        </li>
        <li>
          <strong>Stop bulk email.</strong> Property customers can stop a contractor&apos;s bulk
          email with the unsubscribe link in any such email, or by asking the contractor. The link
          carries no personal information, only a signed customer number. We keep the date and how
          email was turned off with the customer record, so the contractor knows not to turn it
          back on unasked. Storm arrival alert emails stop too; transactional email such as a
          welcome or portal email still goes out.
        </li>
      </ul>
      <p>
        Depending on where you live you may have additional rights over your personal
        information. Write to us and we will honour any request the law gives you.
      </p>

      <h2>Children</h2>
      <p>
        SnowTrack™ is a tool for businesses and is not intended for anyone under 16. We do not
        knowingly collect information from children. If you believe a child&apos;s information has
        reached us, tell us and we will remove it.
      </p>

      <h2>Changes to this policy</h2>
      <p>
        We will update this page when the product changes what it collects or who it sends data
        to, and we will move the &quot;last updated&quot; date. For a change that materially
        affects you, we will email the address on your account.
      </p>

      <h2>Contact us</h2>
      <p>
        For any privacy question, data request or deletion request, email{' '}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. We aim to answer within five
        business days. See also our <Link to="/terms">Terms of Service</Link>.
      </p>
    </LegalLayout>
  );
}

export default Privacy;
